/** Search bar input with autocomplete dropdown. */

import { h } from 'preact';
import { useState, useRef, useEffect, useCallback, useMemo } from 'preact/hooks';
import { Autocomplete, getSearchHistory, clearSearchHistory, removeFromSearchHistory } from './Autocomplete';
import { InstantSearchPanel } from './InstantSearchPanel';
import { RecentSearches } from './RecentSearches';
import type { UseSearchReturn } from '../hooks/useSearch';

/** Read customer first name from Magento's localStorage cache. */
function getCustomerName(): string {
  try {
    const raw = localStorage.getItem('mage-cache-storage');
    if (!raw) return '';
    const data = JSON.parse(raw);
    return data?.customer?.firstname || '';
  } catch {
    return '';
  }
}

interface SearchBarProps {
  onSearch: (query: string) => void;
  autocompleteHook: UseSearchReturn;
  initialValue: string;
  compact?: boolean;
}

export function SearchBar({ onSearch, autocompleteHook, initialValue, compact }: SearchBarProps) {
  const [value, setValue] = useState(initialValue);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [history, setHistory] = useState<string[]>([]);
  const [recentsDismissed, setRecentsDismissed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Animated typing placeholder that cycles through example searches.
  // Clears on focus, resumes on blur if input is empty.
  const EXAMPLES = [
    { label: 'search products', query: 'cleanroom doors' },
    { label: 'search by part #', query: '6602-69-PC' },
    { label: 'search by spec', query: 'ISO 5 stainless steel' },
    { label: 'search products', query: 'desiccator cabinets' },
    { label: 'search by part #', query: '2805-61' },
    { label: 'search products', query: 'laminar flow hoods' },
  ];
  const [placeholder, setPlaceholder] = useState('');
  const [inputFocused, setInputFocused] = useState(false);
  const typingRef = useRef(true);

  useEffect(() => {
    const name = getCustomerName();
    const greeting = name ? `Hi ${name}, ` : '';
    let exIdx = 0;
    let charIdx = 0;
    let deleting = false;
    let paused = false;

    function tick() {
      if (!typingRef.current) return;

      const ex = EXAMPLES[exIdx];
      const full = `${greeting}${ex.label} \u201C${ex.query}\u201D`;

      if (paused) {
        paused = false;
        deleting = true;
        setTimeout(tick, 40);
        return;
      }

      if (!deleting) {
        charIdx++;
        setPlaceholder(full.slice(0, charIdx));
        if (charIdx >= full.length) {
          paused = true;
          setTimeout(tick, 2500);
          return;
        }
        setTimeout(tick, 55 + Math.random() * 35);
      } else {
        charIdx--;
        if (charIdx <= 0) {
          deleting = false;
          exIdx = (exIdx + 1) % EXAMPLES.length;
          setTimeout(tick, 300);
          return;
        }
        setPlaceholder(full.slice(0, charIdx));
        setTimeout(tick, 20);
      }
    }

    const startTimer = setTimeout(tick, 600);
    return () => { typingRef.current = false; clearTimeout(startTimer); };
  }, []);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    doAutocomplete, clearAutocomplete, autocompleteResults, isAutocompleteLoading,
    doInstantSearch, clearInstantResults, instantResults, isInstantSearchLoading,
    loadMoreInstant, isInstantLoadingMore,
  } = autocompleteHook;

  const suggestions = autocompleteResults?.suggestions ?? [];
  const categories = autocompleteResults?.categories ?? [];
  const popular = autocompleteResults?.popular ?? [];

  // Recent searches surface on empty focus — mutually exclusive with the
  // instant/autocomplete dropdowns, which require ≥2 typed characters.
  const showRecents = inputFocused && !recentsDismissed && value.trim().length === 0 && history.length > 0;

  const handleSuggestionClick = useCallback(
    (name: string) => {
      setValue(name);
      setShowDropdown(false);
      clearAutocomplete();
      clearInstantResults();
      onSearch(name);
    },
    [onSearch, clearAutocomplete, clearInstantResults],
  );

  // Handle input change — trigger autocomplete + instant search
  const handleInput = useCallback(
    (e: Event) => {
      const v = (e.target as HTMLInputElement).value;
      setValue(v);
      setSelectedIndex(-1);
      setRecentsDismissed(false);
      if (v.trim().length >= 2) {
        setShowDropdown(true);
        doAutocomplete(v);
        doInstantSearch(v);
      } else {
        setShowDropdown(false);
        clearAutocomplete();
        clearInstantResults();
      }
    },
    [doAutocomplete, doInstantSearch, clearAutocomplete, clearInstantResults],
  );

  // Handle submit
  const handleSubmit = useCallback(
    (e?: Event) => {
      e?.preventDefault();
      if (value.trim()) {
        setShowDropdown(false);
        clearAutocomplete();
        clearInstantResults();
        onSearch(value.trim());
      }
    },
    [value, onSearch, clearAutocomplete, clearInstantResults],
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Recent-searches panel (empty input) gets its own nav.
      if (showRecents) {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setSelectedIndex((i) => (i < history.length - 1 ? i + 1 : 0));
            break;
          case 'ArrowUp':
            e.preventDefault();
            setSelectedIndex((i) => (i > 0 ? i - 1 : history.length - 1));
            break;
          case 'Enter':
            if (selectedIndex >= 0 && selectedIndex < history.length) {
              e.preventDefault();
              handleSuggestionClick(history[selectedIndex]);
            }
            break;
          case 'Escape':
            setRecentsDismissed(true);
            setSelectedIndex(-1);
            break;
        }
        return;
      }

      if (!showDropdown) {
        if (e.key === 'Enter') handleSubmit();
        return;
      }

      // When instant panel is showing, Enter = view all, Escape = close
      if (instantResults) {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleSubmit();
        } else if (e.key === 'Escape') {
          setShowDropdown(false);
          clearAutocomplete();
          clearInstantResults();
        }
        return;
      }

      // Autocomplete-only keyboard navigation
      if (suggestions.length === 0) {
        if (e.key === 'Enter') handleSubmit();
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((i) => (i < suggestions.length - 1 ? i + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((i) => (i > 0 ? i - 1 : suggestions.length - 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
            const item = suggestions[selectedIndex];
            setValue(item.name);
            setShowDropdown(false);
            clearAutocomplete();
            clearInstantResults();
            onSearch(item.name);
          } else {
            handleSubmit();
          }
          break;
        case 'Escape':
          setShowDropdown(false);
          clearAutocomplete();
          clearInstantResults();
          break;
      }
    },
    [showDropdown, showRecents, history, suggestions, selectedIndex, instantResults, handleSubmit, handleSuggestionClick, onSearch, clearAutocomplete, clearInstantResults],
  );

  // Close dropdown and restore placeholder on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const path = e.composedPath();
      if (containerRef.current && !path.includes(containerRef.current)) {
        setShowDropdown(false);
        setInputFocused(false);
      }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, []);

  const handleClear = useCallback(() => {
    setValue('');
    setShowDropdown(false);
    clearAutocomplete();
    clearInstantResults();
    inputRef.current?.focus();
  }, [clearAutocomplete, clearInstantResults]);

  const handleViewAll = useCallback(() => {
    setShowDropdown(false);
    clearAutocomplete();
    clearInstantResults();
    if (value.trim()) onSearch(value.trim());
  }, [value, onSearch, clearAutocomplete, clearInstantResults]);

  return (
    <div class={`ts-search-bar ${compact ? 'ts-search-bar--compact' : ''}`} ref={containerRef}>
      <form class="ts-search-form" onSubmit={handleSubmit} role="search">
        <div class="ts-search-input-wrapper">
          {/* Search icon */}
          <svg class="ts-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>

          <input
            ref={inputRef}
            type="text"
            class="ts-search-input"
            placeholder={inputFocused ? 'Search...' : placeholder}
            value={value}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              setInputFocused(true);
              // Reload on every focus so submits from this tab (and other
              // tabs) are reflected without a page reload.
              setHistory(getSearchHistory());
              setRecentsDismissed(false);
              setSelectedIndex(-1);
              if (value.trim().length >= 2 && (suggestions.length > 0 || instantResults)) {
                setShowDropdown(true);
              }
            }}
            onClick={() => {
              // Re-open recents on click while already focused (Escape
              // dismissal shouldn't be sticky against an explicit click).
              if (!value.trim()) {
                setHistory(getSearchHistory());
                setRecentsDismissed(false);
              }
            }}
            autocomplete="off"
            aria-label="Search products"
            aria-autocomplete="list"
            aria-expanded={showDropdown || showRecents}
            aria-controls="ts-autocomplete-list"
          />

          {/* Loading spinner */}
          {(isAutocompleteLoading || isInstantSearchLoading) && (
            <div class="ts-search-spinner" />
          )}

          {/* Clear button */}
          {value && !isAutocompleteLoading && !isInstantSearchLoading && (
            <button type="button" class="ts-search-clear" onClick={handleClear} aria-label="Clear search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          )}

          <button type="submit" class="ts-search-submit" aria-label="Search">
            Search
          </button>
        </div>
      </form>

      {/* Empty-focus dropdown: the visitor's recent searches */}
      {showRecents && (
        <RecentSearches
          items={history}
          selectedIndex={selectedIndex}
          onSelect={handleSuggestionClick}
          onClear={() => {
            clearSearchHistory();
            setHistory([]);
            inputRef.current?.focus();
          }}
          onRemove={(q) => {
            removeFromSearchHistory(q);
            setHistory(getSearchHistory());
            setSelectedIndex(-1);
            inputRef.current?.focus();
          }}
        />
      )}

      {/* Dropdown: InstantSearchPanel (when results ready) or Autocomplete fallback */}
      {showDropdown && (instantResults || isInstantSearchLoading) && (
        <InstantSearchPanel
          suggestions={suggestions}
          categories={categories}
          popular={popular}
          results={instantResults}
          isSearchLoading={isInstantSearchLoading}
          isLoadingMore={isInstantLoadingMore}
          isAutocompleteLoading={isAutocompleteLoading}
          query={value}
          onSelect={handleSuggestionClick}
          onViewAll={handleViewAll}
          onLoadMore={loadMoreInstant}
        />
      )}
      {showDropdown && !instantResults && !isInstantSearchLoading && (
        <Autocomplete
          suggestions={suggestions}
          categories={categories}
          popular={popular}
          query={value}
          selectedIndex={selectedIndex}
          onSelect={handleSuggestionClick}
          isLoading={isAutocompleteLoading}
        />
      )}
    </div>
  );
}
